/**
 * symbolIndex.ts — Isomorphic cross-file symbol indexer for LSP-like features.
 * Works in both browser (CodeMirror integration) and Cloudflare Workers (AI context).
 *
 * Builds a lightweight symbol table from project files using regex-based parsing.
 * Extends patterns from importResolver.ts with line/column positions and richer metadata.
 */

// ── Types ────────────────────────────────────────────────────────────

export type SymbolKind =
  | 'function'
  | 'class'
  | 'component'
  | 'variable'
  | 'type'
  | 'interface'
  | 'enum'
  | 'hook'
  | 'constant';

export interface SymbolEntry {
  name: string;
  kind: SymbolKind;
  fileName: string;
  filePath: string;
  line: number;      // 1-based
  column: number;    // 0-based
  exported: boolean;
  isDefault: boolean;
  signature?: string; // e.g. "(props: ButtonProps) => JSX.Element"
}

export interface SymbolReference {
  symbolName: string;
  fileName: string;
  filePath: string;
  line: number;
  column: number;
  kind: 'usage' | 'import' | 'export';
}

export interface FileSymbols {
  fileName: string;
  filePath: string;
  definitions: SymbolEntry[];
  imports: Array<{ name: string; localName: string; source: string; line: number }>;
  exports: string[];
}

export interface SymbolIndex {
  /** All symbol definitions across all files */
  definitions: Map<string, SymbolEntry[]>;
  /** Per-file symbol info */
  files: Map<string, FileSymbols>;
  /** Quick lookup: symbol name → defining file(s) */
  definitionIndex: Map<string, SymbolEntry>;
  /** All references to each symbol */
  references: Map<string, SymbolReference[]>;
}

export interface FileInput {
  fileName: string;
  filePath?: string | null;
  content?: string | null;
}

// ── Helpers ──────────────────────────────────────────────────────────

function lineColAt(code: string, offset: number): { line: number; column: number } {
  let line = 1;
  let lastNewline = -1;
  for (let i = 0; i < offset && i < code.length; i++) {
    if (code[i] === '\n') {
      line++;
      lastNewline = i;
    }
  }
  return { line, column: offset - lastNewline - 1 };
}

function isCodeFile(fileName: string): boolean {
  const ext = fileName.split('.').pop()?.toLowerCase();
  return !!ext && ['tsx', 'ts', 'jsx', 'js', 'mjs', 'mts'].includes(ext);
}

function inferKind(name: string, context: string): SymbolKind {
  if (/^use[A-Z]/.test(name)) return 'hook';
  if (/^[A-Z]/.test(name) && /(?:jsx|tsx)/.test(context)) return 'component';
  if (/^[A-Z]/.test(name)) return 'component';
  if (/^[A-Z_]+$/.test(name)) return 'constant';
  return 'function';
}

// ── Per-file extraction ──────────────────────────────────────────────

function extractFileSymbols(fileName: string, filePath: string, code: string): FileSymbols {
  const definitions: SymbolEntry[] = [];
  const imports: FileSymbols['imports'] = [];
  const exports: string[] = [];
  let m: RegExpExecArray | null;

  // ── Imports ──
  const importRe = /^import\s+(?:type\s+)?(?:([\w$]+)\s*,?\s*)?\{?\s*([^}'"]*?)\s*\}?\s*from\s*["']([^"']+)["']/gm;
  while ((m = importRe.exec(code)) !== null) {
    const { line } = lineColAt(code, m.index);
    const source = m[3];
    if (m[1]) imports.push({ name: m[1], localName: m[1], source, line });
    if (m[2]) {
      for (const part of m[2].split(',')) {
        const trimmed = part.trim();
        if (!trimmed) continue;
        const asMatch = trimmed.match(/(\w+)\s+as\s+(\w+)/);
        if (asMatch) {
          imports.push({ name: asMatch[1], localName: asMatch[2], source, line });
        } else if (/^\w+$/.test(trimmed)) {
          imports.push({ name: trimmed, localName: trimmed, source, line });
        }
      }
    }
  }

  // Namespace imports: import * as X from "..."
  const nsRe = /^import\s+\*\s+as\s+(\w+)\s+from\s*["']([^"']+)["']/gm;
  while ((m = nsRe.exec(code)) !== null) {
    const { line } = lineColAt(code, m.index);
    imports.push({ name: '*', localName: m[1], source: m[2], line });
  }

  // ── Exported functions ──
  const exportFuncRe = /^export\s+(default\s+)?(?:async\s+)?function\s+(\w+)\s*(\([^)]*\))?/gm;
  while ((m = exportFuncRe.exec(code)) !== null) {
    const { line, column } = lineColAt(code, m.index);
    const name = m[2];
    const isDefault = !!m[1];
    const kind = inferKind(name, fileName);
    const signature = m[3] || undefined;
    definitions.push({ name, kind, fileName, filePath, line, column, exported: true, isDefault, signature });
    exports.push(name);
  }

  // ── Exported classes ──
  const exportClassRe = /^export\s+(default\s+)?class\s+(\w+)/gm;
  while ((m = exportClassRe.exec(code)) !== null) {
    const { line, column } = lineColAt(code, m.index);
    definitions.push({
      name: m[2], kind: 'class', fileName, filePath, line, column,
      exported: true, isDefault: !!m[1],
    });
    exports.push(m[2]);
  }

  // ── Exported const/let/var (including arrow functions) ──
  const exportVarRe = /^export\s+(default\s+)?(?:const|let|var)\s+(\w+)\s*(?::\s*[\w<>[\]|&\s,()=>]+)?\s*=/gm;
  while ((m = exportVarRe.exec(code)) !== null) {
    const { line, column } = lineColAt(code, m.index);
    const name = m[2];
    const kind = inferKind(name, fileName);
    definitions.push({ name, kind, fileName, filePath, line, column, exported: true, isDefault: !!m[1] });
    exports.push(name);
  }

  // ── Exported interfaces ──
  const exportInterfaceRe = /^export\s+interface\s+(\w+)/gm;
  while ((m = exportInterfaceRe.exec(code)) !== null) {
    const { line, column } = lineColAt(code, m.index);
    definitions.push({
      name: m[1], kind: 'interface', fileName, filePath, line, column,
      exported: true, isDefault: false,
    });
    exports.push(m[1]);
  }

  // ── Exported type aliases ──
  const exportTypeRe = /^export\s+type\s+(\w+)\s*[=<]/gm;
  while ((m = exportTypeRe.exec(code)) !== null) {
    const { line, column } = lineColAt(code, m.index);
    definitions.push({
      name: m[1], kind: 'type', fileName, filePath, line, column,
      exported: true, isDefault: false,
    });
    exports.push(m[1]);
  }

  // ── Exported enums ──
  const exportEnumRe = /^export\s+(const\s+)?enum\s+(\w+)/gm;
  while ((m = exportEnumRe.exec(code)) !== null) {
    const { line, column } = lineColAt(code, m.index);
    definitions.push({
      name: m[2], kind: 'enum', fileName, filePath, line, column,
      exported: true, isDefault: false,
    });
    exports.push(m[2]);
  }

  // ── Non-exported top-level declarations (still useful for same-file references) ──
  const localFuncRe = /^(?:async\s+)?function\s+(\w+)\s*(\([^)]*\))?/gm;
  while ((m = localFuncRe.exec(code)) !== null) {
    const name = m[1];
    if (exports.includes(name)) continue; // already captured as export
    // Check it's not inside export (which we already handled)
    const before = code.slice(Math.max(0, m.index - 10), m.index);
    if (/export\s+$/.test(before)) continue;
    const { line, column } = lineColAt(code, m.index);
    const kind = inferKind(name, fileName);
    definitions.push({ name, kind, fileName, filePath, line, column, exported: false, isDefault: false, signature: m[2] || undefined });
  }

  const localClassRe = /^class\s+(\w+)/gm;
  while ((m = localClassRe.exec(code)) !== null) {
    const name = m[1];
    if (exports.includes(name)) continue;
    const before = code.slice(Math.max(0, m.index - 10), m.index);
    if (/export\s+$/.test(before)) continue;
    const { line, column } = lineColAt(code, m.index);
    definitions.push({ name, kind: 'class', fileName, filePath, line, column, exported: false, isDefault: false });
  }

  const localInterfaceRe = /^interface\s+(\w+)/gm;
  while ((m = localInterfaceRe.exec(code)) !== null) {
    const name = m[1];
    if (exports.includes(name)) continue;
    const before = code.slice(Math.max(0, m.index - 10), m.index);
    if (/export\s+$/.test(before)) continue;
    const { line, column } = lineColAt(code, m.index);
    definitions.push({ name, kind: 'interface', fileName, filePath, line, column, exported: false, isDefault: false });
  }

  const localTypeRe = /^type\s+(\w+)\s*[=<]/gm;
  while ((m = localTypeRe.exec(code)) !== null) {
    const name = m[1];
    if (exports.includes(name)) continue;
    const before = code.slice(Math.max(0, m.index - 10), m.index);
    if (/export\s+$/.test(before)) continue;
    const { line, column } = lineColAt(code, m.index);
    definitions.push({ name, kind: 'type', fileName, filePath, line, column, exported: false, isDefault: false });
  }

  // Top-level const/let/var (not exported, not inside functions)
  const localVarRe = /^(?:const|let|var)\s+(\w+)\s*(?::\s*[\w<>[\]|&\s,()=>]+)?\s*=/gm;
  while ((m = localVarRe.exec(code)) !== null) {
    const name = m[1];
    if (exports.includes(name)) continue;
    const before = code.slice(Math.max(0, m.index - 10), m.index);
    if (/export\s+$/.test(before)) continue;
    const { line, column } = lineColAt(code, m.index);
    const kind = inferKind(name, fileName);
    definitions.push({ name, kind, fileName, filePath, line, column, exported: false, isDefault: false });
  }

  // ── Re-exports: export { X, Y } from "./other" ──
  const reExportRe = /^export\s+\{([^}]+)\}\s*from\s*["']([^"']+)["']/gm;
  while ((m = reExportRe.exec(code)) !== null) {
    for (const part of m[1].split(',')) {
      const trimmed = part.trim();
      const asMatch = trimmed.match(/(\w+)\s+as\s+(\w+)/);
      const name = asMatch ? asMatch[2] : trimmed;
      if (name && /^\w+$/.test(name)) exports.push(name);
    }
  }

  return { fileName, filePath, definitions, imports, exports };
}

// ── Build cross-file references ──────────────────────────────────────

function buildReferences(
  filesMap: Map<string, FileSymbols>,
  allCode: Map<string, string>,
): Map<string, SymbolReference[]> {
  const refs = new Map<string, SymbolReference[]>();

  for (const [filePath, fileSymbols] of filesMap) {
    const code = allCode.get(filePath);
    if (!code) continue;

    // References from imports
    for (const imp of fileSymbols.imports) {
      const list = refs.get(imp.localName) || [];
      list.push({
        symbolName: imp.name,
        fileName: fileSymbols.fileName,
        filePath: fileSymbols.filePath,
        line: imp.line,
        column: 0,
        kind: 'import',
      });
      refs.set(imp.localName, list);
    }

    // References from JSX usage and identifiers
    const jsxRe = /<([A-Z][A-Za-z0-9]*)/g;
    let m: RegExpExecArray | null;
    while ((m = jsxRe.exec(code)) !== null) {
      const name = m[1];
      const { line, column } = lineColAt(code, m.index + 1); // +1 for <
      const list = refs.get(name) || [];
      list.push({
        symbolName: name,
        fileName: fileSymbols.fileName,
        filePath: fileSymbols.filePath,
        line,
        column,
        kind: 'usage',
      });
      refs.set(name, list);
    }

    // Function/hook call references
    const callRe = /\b(use[A-Z]\w+|[A-Z][a-z]\w+)\s*\(/g;
    while ((m = callRe.exec(code)) !== null) {
      const name = m[1];
      const { line, column } = lineColAt(code, m.index);
      const list = refs.get(name) || [];
      // Avoid duplicating with JSX matches
      if (!list.some((r) => r.line === line && r.column === column && r.filePath === filePath)) {
        list.push({
          symbolName: name,
          fileName: fileSymbols.fileName,
          filePath: fileSymbols.filePath,
          line,
          column,
          kind: 'usage',
        });
        refs.set(name, list);
      }
    }
  }

  return refs;
}

// ── Main: Build symbol index ─────────────────────────────────────────

export function buildSymbolIndex(files: FileInput[]): SymbolIndex {
  const definitions = new Map<string, SymbolEntry[]>();
  const filesMap = new Map<string, FileSymbols>();
  const definitionIndex = new Map<string, SymbolEntry>();
  const allCode = new Map<string, string>();

  for (const file of files) {
    if (!file.content || !isCodeFile(file.fileName)) continue;
    const filePath = file.filePath || `/${file.fileName}`;
    const fileSymbols = extractFileSymbols(file.fileName, filePath, file.content);
    filesMap.set(filePath, fileSymbols);
    allCode.set(filePath, file.content);

    for (const def of fileSymbols.definitions) {
      const list = definitions.get(def.name) || [];
      list.push(def);
      definitions.set(def.name, list);

      // For the quick lookup, prefer exported definitions
      const existing = definitionIndex.get(def.name);
      if (!existing || (def.exported && !existing.exported)) {
        definitionIndex.set(def.name, def);
      }
    }
  }

  const references = buildReferences(filesMap, allCode);

  return { definitions, files: filesMap, definitionIndex, references };
}

// ── Lookup functions ─────────────────────────────────────────────────

/** Find the definition of a symbol by name */
export function findDefinition(index: SymbolIndex, name: string): SymbolEntry | null {
  return index.definitionIndex.get(name) || null;
}

/** Find all definitions matching a name (there could be multiple across files) */
export function findAllDefinitions(index: SymbolIndex, name: string): SymbolEntry[] {
  return index.definitions.get(name) || [];
}

/** Find all references to a symbol */
export function findReferences(index: SymbolIndex, name: string): SymbolReference[] {
  return index.references.get(name) || [];
}

/** Get all exported symbols from a specific file */
export function getFileExports(index: SymbolIndex, filePath: string): SymbolEntry[] {
  const fileSymbols = index.files.get(filePath);
  if (!fileSymbols) return [];
  return fileSymbols.definitions.filter((d) => d.exported);
}

/** Find symbol at a specific position in a file */
export function findSymbolAtPosition(
  index: SymbolIndex,
  filePath: string,
  line: number,
  column: number,
  code: string,
): SymbolEntry | null {
  // Extract the word at the given position
  if (!code) return null;
  const lines = code.split('\n');
  if (line < 1 || line > lines.length) return null;
  const lineText = lines[line - 1];
  if (!lineText) return null;

  // Find word boundaries around the column
  let start = column;
  let end = column;
  while (start > 0 && /\w/.test(lineText[start - 1])) start--;
  while (end < lineText.length && /\w/.test(lineText[end])) end++;
  const word = lineText.slice(start, end);
  if (!word) return null;

  return findDefinition(index, word);
}

// ── AI Context: Generate compact symbol map for LLM injection ────────

export function generateSymbolMap(index: SymbolIndex): string {
  const lines: string[] = ['=== PROJECT SYMBOL MAP ==='];

  for (const [filePath, fileSymbols] of index.files) {
    const exported = fileSymbols.definitions.filter((d) => d.exported);
    if (exported.length === 0) continue;

    const symbolDescriptions = exported.map((d) => {
      const kindLabel = d.kind === 'component' ? 'component'
        : d.kind === 'hook' ? 'hook'
        : d.kind === 'interface' ? 'interface'
        : d.kind === 'type' ? 'type'
        : d.kind === 'enum' ? 'enum'
        : d.kind === 'class' ? 'class'
        : d.kind === 'constant' ? 'const'
        : 'fn';
      return `${d.name} (${kindLabel})`;
    });

    lines.push(`${fileSymbols.fileName}: exports ${symbolDescriptions.join(', ')}`);
  }

  // Add import relationships (which files import from which)
  lines.push('');
  lines.push('--- Import Graph ---');
  for (const [, fileSymbols] of index.files) {
    const localImports = fileSymbols.imports.filter((i) => i.source.startsWith('.'));
    if (localImports.length === 0) continue;

    const bySource = new Map<string, string[]>();
    for (const imp of localImports) {
      const list = bySource.get(imp.source) || [];
      list.push(imp.localName);
      bySource.set(imp.source, list);
    }

    const importDescriptions = [...bySource.entries()]
      .map(([src, names]) => `${names.join(', ')} (from ${src})`)
      .join('; ');

    lines.push(`${fileSymbols.fileName}: imports ${importDescriptions}`);
  }

  lines.push('=== END SYMBOL MAP ===');
  return lines.join('\n');
}

// ── Incremental update ───────────────────────────────────────────────

/** Update a single file in the index without rebuilding everything */
export function updateFileInIndex(
  index: SymbolIndex,
  file: FileInput,
): void {
  if (!file.content || !isCodeFile(file.fileName)) return;
  const filePath = file.filePath || `/${file.fileName}`;

  // Remove old definitions from this file
  const oldFileSymbols = index.files.get(filePath);
  if (oldFileSymbols) {
    for (const def of oldFileSymbols.definitions) {
      const list = index.definitions.get(def.name);
      if (list) {
        const filtered = list.filter((d) => d.filePath !== filePath);
        if (filtered.length === 0) {
          index.definitions.delete(def.name);
          index.definitionIndex.delete(def.name);
        } else {
          index.definitions.set(def.name, filtered);
          // Update quick lookup if we removed the primary definition
          if (index.definitionIndex.get(def.name)?.filePath === filePath) {
            index.definitionIndex.set(def.name, filtered[0]);
          }
        }
      }
    }
  }

  // Re-extract and add
  const newFileSymbols = extractFileSymbols(file.fileName, filePath, file.content);
  index.files.set(filePath, newFileSymbols);

  for (const def of newFileSymbols.definitions) {
    const list = index.definitions.get(def.name) || [];
    list.push(def);
    index.definitions.set(def.name, list);

    const existing = index.definitionIndex.get(def.name);
    if (!existing || (def.exported && !existing.exported)) {
      index.definitionIndex.set(def.name, def);
    }
  }
}
